import { z } from "zod";

import { TOOL_SIDE_EFFECTS } from "./tool-side-effect";

import { SkillPayloadReferenceSchema } from "./skill-payload";
import { canonicalJsonStringify } from "./canonical-json";

export const AGENT_BUNDLE_SCHEMA_VERSION = 3 as const;
export const AGENT_RUNTIME_MANIFEST_SCHEMA_VERSION = 2 as const;

const Sha256HexSchema = z.string().regex(/^[a-f0-9]{64}$/);
const JsonValueSchema = z.json();

const SkillCompanionFileSchema = z
  .object({
    path: z.string().min(1),
    content: z.string(),
    size_bytes: z.number().int().nonnegative(),
    content_sha256: Sha256HexSchema,
  })
  .strict();

const AgentSkillSnapshotSchema = z
  .object({
    id: z.string().min(1).nullable(),
    scope: z.enum(["global", "workspace", "agent"]),
    name: z.string().min(1),
    description: z.string(),
    authored_content: z.string(),
    rendered_content_sha256: Sha256HexSchema,
    companion_files: z.array(SkillCompanionFileSchema),
  })
  .strict();

export const AgentBundleReferenceFileSchema = z
  .object({
    id: z.string().min(1),
    filename: z.string().min(1),
    label: z.string().nullable(),
    content_type: z.string().min(1),
    content_sha256: Sha256HexSchema,
  })
  .strict();

const AuthoredReferencesSchema = z
  .object({
    /**
     * Ids of retired `port` chips still present in the procedure. Kept under
     * this key, and with no `staged_folders` sibling, because the manifest is
     * content-hashed: adding or renaming a key here changes every existing
     * bundle's hash and fails the pin check. Staged-folder chips are covered
     * by the rendered procedure's own hash; index them in schema version 2.
     */
    ports: z.array(z.string().min(1)),
    agents: z.array(z.string().min(1)),
    tools: z.array(z.string().min(1)),
    files: z.array(z.string().min(1)),
    custom_models: z.array(z.string().min(1)),
    skills: z.array(z.string().min(1)),
    integrations: z.array(z.string().min(1)),
    links: z.array(
      z
        .object({
          provider: z.string().min(1),
          url: z.string().min(1),
        })
        .strict(),
    ),
    custom_model_fields: z.record(z.string(), z.array(z.string().min(1))),
  })
  .strict();

export const AgentBundleManifestV1Schema = z
  .object({
    schema_version: z.literal(1),
    agent: z
      .object({
        kind: z.enum(["user", "system", "code_defined", "grader", "improver"]),
        managed_system_prompt: z.string().nullable(),
        operating_procedure: z.string().nullable(),
        system_prompt: z.string(),
        requested_model: z.string().min(1),
        model_parameters: z.record(z.string(), JsonValueSchema),
        tools_config: z.array(JsonValueSchema),
        config: z.record(z.string(), JsonValueSchema),
        tool_policy: z.record(z.string(), JsonValueSchema),
        authored_references: AuthoredReferencesSchema,
        output_type: z.string().nullable(),
        output_schema: JsonValueSchema.nullable(),
      })
      .strict(),
    skills: z.array(AgentSkillSnapshotSchema),
    reference_files: z.array(AgentBundleReferenceFileSchema),
  })
  .strict();

export type AgentBundleManifestV1 = z.infer<typeof AgentBundleManifestV1Schema>;
export const AgentBundleManifestV2Schema = AgentBundleManifestV1Schema.extend({
  schema_version: z.literal(2),
  skills: z.array(SkillPayloadReferenceSchema),
}).strict();
export type AgentBundleManifestV2 = z.infer<typeof AgentBundleManifestV2Schema>;

/**
 * One live grant the agent held when the version was minted, keyed by its target's public id.
 * Modes stay strings: as on the grant row, only an exact "allow" grants anything.
 */
export const AgentBundleAccessEntrySchema = z
  .object({
    kind: z.enum(["connection", "mcp_server", "linked_account", "mailbox"]),
    id: z.string().min(1),
    permissions: z.record(z.string(), z.string()),
    tool_permissions: z.record(z.string(), z.string()),
  })
  .strict();
export type AgentBundleAccessEntry = z.infer<typeof AgentBundleAccessEntrySchema>;
export const AgentBundleAccessSchema = z.array(AgentBundleAccessEntrySchema);

export const AgentBundleManifestV3Schema = AgentBundleManifestV2Schema.extend({
  schema_version: z.literal(3),
  access: AgentBundleAccessSchema,
}).strict();
export type AgentBundleManifestV3 = z.infer<typeof AgentBundleManifestV3Schema>;
export const AgentBundleManifestSchema = z.discriminatedUnion("schema_version", [
  AgentBundleManifestV1Schema,
  AgentBundleManifestV2Schema,
  AgentBundleManifestV3Schema,
]);
export type AgentBundleManifest = z.infer<typeof AgentBundleManifestSchema>;
export function parseAgentBundleManifest(value: unknown): AgentBundleManifest {
  return AgentBundleManifestSchema.parse(value);
}

export type AgentBundleReferenceFile = z.infer<typeof AgentBundleReferenceFileSchema>;

const ToolAvailabilitySchema = z
  .object({
    status: z.enum(["active", "withheld"]),
    reason: z.string().min(1).optional(),
    detail: z.string().min(1).optional(),
    config_path: z.string().min(1).optional(),
  })
  .strict();

export const AgentToolContractSchema = z
  .object({
    name: z.string().min(1),
    description: z.string(),
    source: z.enum([
      "workspace",
      "sandbox",
      "integration",
      "email",
      "tags",
      "files",
      "output",
      "skill",
      "memory",
      "websearch",
      "custom_models",
      "listeners",
      "codemode",
    ]),
    provider: z.string().min(1).optional(),
    provider_label: z.string().min(1).optional(),
    availability: ToolAvailabilitySchema,
    input_schema: JsonValueSchema.nullable(),
    side_effect: z.enum(TOOL_SIDE_EFFECTS).optional(),
  })
  .strict();

export type AgentToolContract = z.infer<typeof AgentToolContractSchema>;

const ResolvedModelRouteSchema = z
  .object({
    kind: z.enum(["document_gateway", "deployment_pin", "deployment"]),
    gateway_name: z.string().min(1).nullable(),
    gateway_type: z.string().min(1).nullable(),
    remote_model_id: z.string().min(1),
    served_by: z.enum(["platform", "workspace"]),
    credential_mode: z.enum(["platform", "workspace", "uncredentialed"]),
    pricing_source: z.enum(["route_override", "deployment_route", "catalog"]),
  })
  .strict();

export const AgentRuntimeManifestV1Schema = z
  .object({
    schema_version: z.literal(1),
    bundle: z
      .object({
        id: z.string().min(1),
        content_sha256: Sha256HexSchema,
      })
      .strict(),
    execution: z
      .object({
        worker_build: z.string().min(1),
        system_prompt_sha256: Sha256HexSchema,
      })
      .strict(),
    model: z
      .object({
        requested_model: z.string().min(1),
        resolved_model_id: z.string().min(1),
        route: ResolvedModelRouteSchema,
      })
      .strict(),
    tools: z.array(AgentToolContractSchema),
    framework_tools: z.array(AgentToolContractSchema),
    sandbox: z
      .object({
        provider: z.string().min(1),
        runtime_reference: z.string().min(1),
        mutable_reference: z.boolean(),
        network_policy: z.string().min(1),
      })
      .strict(),
    simulation: z
      .object({
        scenario_id: z.string().min(1),
        tool_execution_route: z.string().min(1),
      })
      .strict()
      .nullable(),
    feature_flags: z.record(z.string(), z.union([z.boolean(), z.string(), z.number()])),
  })
  .strict();

export type AgentRuntimeManifestV1 = z.infer<typeof AgentRuntimeManifestV1Schema>;

export const AgentRuntimeManifestV2Schema = AgentRuntimeManifestV1Schema.extend({
  schema_version: z.literal(2),
  resolved_skills: z.array(SkillPayloadReferenceSchema),
}).strict();
export type AgentRuntimeManifestV2 = z.infer<typeof AgentRuntimeManifestV2Schema>;
export const AgentRuntimeManifestSchema = z.discriminatedUnion("schema_version", [
  AgentRuntimeManifestV1Schema,
  AgentRuntimeManifestV2Schema,
]);
export type AgentRuntimeManifest = z.infer<typeof AgentRuntimeManifestSchema>;

export function canonicalAgentBundleManifest(manifest: AgentBundleManifest): string {
  return canonicalJsonStringify(AgentBundleManifestSchema.parse(manifest));
}

export function canonicalAgentRuntimeManifest(manifest: AgentRuntimeManifest): string {
  return canonicalJsonStringify(AgentRuntimeManifestSchema.parse(manifest));
}

export class AgentExecutionManifestValidationError extends Error {
  constructor(kind: "bundle" | "runtime", cause: unknown) {
    const resolution =
      kind === "bundle"
        ? "Publish a new agent version or rerun the agent-version bundle backfill, then retry."
        : "Create a fresh eval case with a current worker, then retry.";
    super(`The agent ${kind} manifest is not valid schema version 1. ${resolution}`, { cause });
    this.name = "AgentExecutionManifestValidationError";
  }
}

export function parseAgentBundleManifestV1(value: unknown): AgentBundleManifestV1 {
  try {
    return AgentBundleManifestV1Schema.parse(value);
  } catch (cause) {
    throw new AgentExecutionManifestValidationError("bundle", cause);
  }
}

export function parseAgentRuntimeManifestV1(value: unknown): AgentRuntimeManifestV1 {
  try {
    return AgentRuntimeManifestV1Schema.parse(value);
  } catch (cause) {
    throw new AgentExecutionManifestValidationError("runtime", cause);
  }
}
